'use client';

import { SvgFlash } from '@bazaar-marketplace/assets/flash';
import { SvgParticipate } from '@bazaar-marketplace/assets/participate';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { match } from 'ts-pattern';
import { SvgClock } from '~/components/icons/clock';

import { DropCountdownTimer } from '~/components/drop-countdown-timer';
import { Button } from '~/components/ui/button/button';
import { ROUTES } from '~/constants/routes';
import { useDropWithStatusStream } from '~/hooks/use-drop-with-status-stream';
import type { DropResponseDto } from '~/lib/api';
import { getDropsControllerGetDropStatsQueryOptions } from '~/lib/api';
import { getTotalPackCount, type DropState } from '~/lib/drop-state';

import { useState } from 'react';
import { DropBadges } from '~/components';
import { useOptionalQuery } from '~/hooks/use-optional-query';
import { DropWhitelistBadge } from '~/components/drop-badges/drop-whitelist-badge';

interface DropDisplayProps {
  drop: DropResponseDto;
  hasParticipated: boolean;
  isActive: boolean;
}

export const DropDisplay = ({ drop, hasParticipated, isActive }: DropDisplayProps) => {
  const t = useTranslations('join-to-get-goodies');
  const [, setNow] = useState(() => Date.now());

  function onTimerEnd() {
    // rerenders so that the current-time-based state updates
    setNow(Date.now());
  }

  const statsQuery = useOptionalQuery(isActive ? getDropsControllerGetDropStatsQueryOptions(drop.id) : undefined);
  const dropStatsData = statsQuery?.data;

  const { dropState } = useDropWithStatusStream({
    drop,
    onDropCompleted: (context) => {
      context.invalidateQueries();
    },
  });

  if (!dropState) {
    return null;
  }

  const renderDropTimer = (state: DropState) =>
    match(state)
      .with({ type: 'processing' }, () => (
        <div className="text-white text-2xl font-bold">{t('status.processing-results')}</div>
      ))
      .with({ type: 'completed' }, () => <div className="text-white text-2xl font-bold">{t('countdown.ended')}</div>)
      .with({ type: 'upcoming' }, { type: 'active' }, { type: 'ending-soon' }, () => (
        <DropCountdownTimer
          className="text-white text-2xl font-bold"
          endTime={drop.endTime}
          startTime={drop.startTime}
          onTimerEnd={onTimerEnd}
        />
      ))
      .exhaustive();

  const renderActionButton = (state: DropState) =>
    match(state)
      .with({ type: 'processing' }, () => (
        <Button className="w-auto h-11 text-md" disabled variant="rainbow">
          <span className="flex gap-2 items-center">{t('button.processing')}</span>
        </Button>
      ))
      .otherwise(() => (
        <Button className="w-auto h-11 text-md" variant="rainbow">
          <Link href={state.type === 'completed' ? ROUTES.CLAIM_DROP(drop.id) : ROUTES.JOIN_TO_GET_GOODIES(drop.id)}>
            {match(state)
              .with({ type: 'completed' }, () => (
                <span className="flex gap-2 items-center">{t('button.try-luck')}</span>
              ))
              .with({ type: 'upcoming' }, () => (
                <span className="flex gap-2 items-center">
                  <SvgClock />
                  {t('button.discover')}
                </span>
              ))
              .when(
                () => hasParticipated,
                () => (
                  <span className="flex gap-2 items-center">
                    <SvgFlash /> {t('button.boost-odds')}
                  </span>
                ),
              )
              .otherwise(() => (
                <span className="flex gap-2 items-center">
                  <SvgParticipate />
                  {t('button.participate')}
                </span>
              ))}
          </Link>
        </Button>
      ));

  const shouldShowBadges = dropState.type !== 'upcoming';

  return (
    <div className="relative z-20 flex flex-col items-center justify-center pt-51">
      <div className="flex flex-col items-center justify-end gap-6 w-full mt-auto">
        {/* Title and Badges */}
        <div className="flex flex-col items-center gap-4 w-full">
          <h1 className="text-white text-3xl font-bold text-center font-quicksand">
            <span>{drop.name}</span>

            <DropWhitelistBadge dropId={drop.id} />
          </h1>

          {shouldShowBadges ? (
            <DropBadges
              totalLockedAmount={dropStatsData?.data.totalLockedAmount}
              totalLockedPacks={dropStatsData?.data.totalLockedPacks}
            />
          ) : (
            <div className="h-[30px]" />
          )}
        </div>

        {/* Countdown and CTA */}
        <div className="flex flex-col items-center space-y-3">
          <div className="mb-6 flex items-center justify-center">{renderDropTimer(dropState)}</div>

          {renderActionButton(dropState)}

          <p className="text-white/60 text-sm font-bold">
            {t('status.only-packs', { count: getTotalPackCount(drop.dropCollections) })}
          </p>
        </div>
      </div>
    </div>
  );
};
